import * as React from 'react'

import { Alert, Box, Button, IllustrationType, Text } from '@factorialco/deprecated-design-system'
import copy from 'copy-to-clipboard'

import Modal from 'modules/core/components/Modal'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface AddFundsModalProps {
  onClose: () => void
  accountNumber: string | null | undefined
  bic: string | null | undefined
  legalEntityName?: string | null
  beneficiaryName?: string
  bankAddress?: string
  mode: 'addFunds' | 'insufficientFunds'
  fundsNeeded?: string
  extraFieldLabel: string
}

interface CardProps {
  value: string
  title: string
  content: React.ReactNode
}

const CopyButton = ({ value }: { value: string }) => {
  const onCopy = (valueToCopy: string) => {
    copy(valueToCopy)
    app.setFlash({
      type: 'correct',
      message: i18n.t('banking.add_funds_modal.copied_successfully'),
    })
  }

  return <Button type="secondary" onClick={() => onCopy(value)} iconName="layers-front" />
}

const Card = ({ value, title, content }: CardProps) => (
  <Box
    flexDirection="row"
    gap="s8"
    padding="s24"
    alignItems="center"
    borderRadius={{ all: 'abs008' }}
    border={{
      all: {
        color: 'grey400',
        width: 's1',
        style: 'solid',
      },
    }}
  >
    <Box width="full">
      <Text.Normal weight="semibold" color="grey800">
        {title}
      </Text.Normal>
      <Text.Normal color="grey800">{content}</Text.Normal>
    </Box>
    <CopyButton value={value} />
  </Box>
)

const AddFundsModal = ({
  onClose,
  accountNumber,
  bic,
  legalEntityName,
  beneficiaryName,
  bankAddress,
  mode,
  fundsNeeded,
  extraFieldLabel,
}: AddFundsModalProps) => {
  const accountNumberChunks = accountNumber?.split(/(.{4})/).filter((chunk) => chunk)

  let title, description, legalEntityText
  let illustration: IllustrationType
  if (legalEntityName) {
    legalEntityText = <Text.Normal color="grey700">{legalEntityName}</Text.Normal>
  }

  if (mode === 'insufficientFunds') {
    title = i18n.t('banking.add_funds_modal.insufficient_funds.title')
    description = i18n.t('banking.add_funds_modal.insufficient_funds.description', { fundsNeeded })
    illustration = 'alert'
  } else {
    title = i18n.t('banking.add_funds_modal.add_funds.title')
    description = i18n.t('expenses_v2.transactions.add_funds_modal.description')
    illustration = 'wallet'
  }

  return (
    <Modal
      title={title}
      description={legalEntityText}
      illustration={illustration}
      onClose={onClose}
    >
      <Box flexDirection="column" gap="s24">
        <Text.Normal>{description}</Text.Normal>
        <Box flexDirection="column" gap="s12">
          {beneficiaryName && (
            <Card
              title={i18n.t('banking.attributes.beneficiary_name')}
              content={beneficiaryName}
              value={beneficiaryName}
            />
          )}
          {accountNumberChunks && accountNumber && (
            <Card
              title={i18n.t('banking.attributes.account_number')}
              content={
                <Box flexDirection="row" gap="s4">
                  {accountNumberChunks.map((chunk) => {
                    return (
                      <Box key={chunk}>
                        <Text.Normal>{chunk}</Text.Normal>
                      </Box>
                    )
                  })}
                </Box>
              }
              value={accountNumber}
            />
          )}
          {bic && <Card title={extraFieldLabel} content={bic} value={bic} />}
          {bankAddress && (
            <Card
              title={i18n.t('expenses_v2.transactions.add_funds_modal.address')}
              value={bankAddress}
              content={bankAddress}
            />
          )}
        </Box>
        <Alert color="note">{i18n.t('banking.add_funds_modal.alert')}</Alert>
      </Box>
    </Modal>
  )
}

export default AddFundsModal
